/**
 * The facts check — a short, method-neutral pre/post measure of multiplication and division facts, tables 2–10,
 * for the research record (Meliesa, Nov 4, 2026: "not testing any patterns or Tigard Method materials — just the
 * skills"). Standard form, no scaffolds, no hints, no feedback, a skip button; each item is timed silently.
 * Two fixed forms of the same shape with different items: A at the beginning, B at the end. Every student who
 * takes it gets the same items in the same order, so results compare across students and across classes.
 */
import type { Item, Lesson } from "./types";
import type { StudentProgress } from "../engine/progress";

export const TABLES = [2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
/** Items per table: 4 multiplication (36 in all) and 3 division (27). */
export const MUL_PER_TABLE = 4, DIV_PER_TABLE = 3;
/** A right answer this fast counts as fluent (the same bar as the fact map). */
export const FLUENT_MS = 3000;
export type Form = "A" | "B";
export type When = "pre" | "post";
export const FORM_OF: Record<When, Form> = { pre: "A", post: "B" };

export interface FactsItem extends Item { op: "mul" | "div"; table: number; other: number }

import type { FactsResult } from "../engine/progress";
export type { FactsResult };

/* A tiny seeded generator, so each form is the same every time. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function shuffle<T>(xs: readonly T[], r: () => number): T[] { const a = [...xs]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; } return a; }

function mul(form: Form, a: number, b: number, table: number): FactsItem {
  return { id: `ft-${form}-m-${a}x${b}`, skill: "facts-test", op: "mul", table, other: table === a ? b : a, prompt: `${a} × ${b} = ?`, input: { kind: "number" }, answer: String(a * b), hints: [] };
}
function div(form: Form, t: number, q: number): FactsItem {
  return { id: `ft-${form}-d-${t * q}/${t}`, skill: "facts-test", op: "div", table: t, other: q, prompt: `${t * q} ÷ ${t} = ?`, input: { kind: "number" }, answer: String(q), hints: [] };
}

/**
 * Multiplication: 4 items per table, and no fact twice on a form (7 × 8 and 8 × 7 are one fact). There are only 45
 * distinct facts in 2–10, so two forms of 36 must share some; form B takes the facts form A didn't use first, and any
 * fact both forms need is shown the other way round on B (8 × 7 for A's 7 × 8). Division: 3 per divisor, no overlap.
 */
function buildMul(form: Form, avoid: Map<string, number>): FactsItem[] {
  const seed = form === "A" ? 20261104 : 20270601;
  const key = (a: number, b: number) => `${Math.min(a, b)}x${Math.max(a, b)}`;
  // Pick 4 partners per table so that no fact is used twice, preferring facts the other form didn't use.
  // A small backtracking search in a fixed random order, so the result is the same every time.
  const choice: number[][] = [];
  const used = new Set<string>();
  const solve = (k: number): boolean => {
    if (k === TABLES.length) return true;
    const t = TABLES[k]!;
    const cands = shuffle(TABLES, rng(seed + t)).filter((b) => !used.has(key(t, b))).sort((a, b) => Number(avoid.has(key(t, a))) - Number(avoid.has(key(t, b))));
    const pick = (chosen: number[], from: number): boolean => {
      if (chosen.length === MUL_PER_TABLE) { choice[k] = chosen; if (solve(k + 1)) return true; return false; }
      for (let i = from; i < cands.length; i++) {
        const b = cands[i]!;
        used.add(key(t, b));
        if (pick([...chosen, b], i + 1)) return true;
        used.delete(key(t, b));
      }
      return false;
    };
    return pick([], 0);
  };
  if (!solve(0)) throw new Error("facts check: no assignment");
  const items: FactsItem[] = [];
  TABLES.forEach((t, k) => choice[k]!.forEach((b, i) => {
    // Both orders: alternate which number comes first. A shared fact is shown the other way round on B.
    let tableFirst = i % 2 === 0;
    const first = avoid.get(key(t, b));
    if (first !== undefined) tableFirst = first !== t;
    items.push(tableFirst ? mul(form, t, b, t) : mul(form, b, t, t));
  }));
  return items;
}
function buildDiv(form: Form): FactsItem[] {
  const items: FactsItem[] = [];
  for (const t of TABLES) {
    // The same shuffle for both forms: A takes the first three quotients, B the next three, so no division item is on both.
    const quotients = shuffle(TABLES, rng(20261104 + 100 + t));
    const dq = form === "A" ? quotients.slice(0, DIV_PER_TABLE) : quotients.slice(DIV_PER_TABLE, DIV_PER_TABLE * 2);
    for (const q of dq) items.push(div(form, t, q));
  }
  return items;
}
function build(form: Form, avoid = new Map<string, number>()): FactsItem[] {
  const r = rng(form === "A" ? 7 : 11);
  // Multiplication first (shuffled), then division (shuffled): one kind of thinking at a time.
  return [...shuffle(buildMul(form, avoid), r), ...shuffle(buildDiv(form), r)];
}
/** Each multiplication fact on a form → the number shown first, so the other form can show it the other way round. */
const mulFirst = (items: FactsItem[]) => new Map(items.filter((x) => x.op === "mul").map((x) => [`${Math.min(x.table, x.other)}x${Math.max(x.table, x.other)}`, Number(x.prompt.split(" ")[0])]));
const FORM_A = build("A");
const FORMS: Record<Form, FactsItem[]> = { A: FORM_A, B: build("B", mulFirst(FORM_A)) };
export const formItems = (form: Form): FactsItem[] => FORMS[form];

export interface FactsScore { n: number; right: number; fluent: number; skipped: number; medianMs: number }
export interface FactsSummary { all: FactsScore; mul: FactsScore; div: FactsScore; byTable: Record<number, FactsScore> }

function score(items: FactsItem[], r: FactsResult): FactsScore {
  let right = 0, fluent = 0, skipped = 0;
  const ms: number[] = [];
  for (const it of items) {
    const x = r.items[it.id];
    if (!x || x.c === null) { skipped++; continue; }
    if (x.c) { right++; ms.push(x.ms); if (x.ms > 0 && x.ms < FLUENT_MS) fluent++; }
  }
  ms.sort((a, b) => a - b);
  return { n: items.length, right, fluent, skipped, medianMs: ms.length ? Math.round(ms[Math.floor(ms.length / 2)]!) : 0 };
}
/** Scores for one result: overall, by operation, by table. */
export function factsSummary(r: FactsResult): FactsSummary {
  const items = formItems(r.form);
  const byTable: Record<number, FactsScore> = {};
  for (const t of TABLES) byTable[t] = score(items.filter((x) => x.table === t), r);
  return { all: score(items, r), mul: score(items.filter((x) => x.op === "mul"), r), div: score(items.filter((x) => x.op === "div"), r), byTable };
}

/** Which sitting is next: the beginning check until it's taken, then the end check. */
export const nextWhen = (p: StudentProgress): When | null => (!p.factsTest?.pre ? "pre" : !p.factsTest.post ? "post" : null);
/** Grades 3 and up take it (and students with no grade on file); K–2 haven't met multiplication. */
export const factsApplies = (grade: number | null | undefined): boolean => grade == null || grade >= 3;

export const FACTS_LESSON: Lesson = {
  id: "facts", level: "all", num: 0, title: "Facts Check", blurb: "multiplication and division facts, 2s through 10s",
  steps: [{ kind: "factstest", id: "factstest", title: "Facts check", intro: "Multiplication and division facts, 2s through 10s. Type what you know; when you don't know one, say so and move on. No tools, no hints — this just shows what you know today." }],
};
